import React, { useState, useRef, useEffect } from 'react';
import { getProjectScheduleTypes, ScheduleTypeInfo } from '../api/client';
import { fuzzyMatchType, toTitleCase } from '../utils/fuzzyMatch';

interface Props {
  projectId: number;
  token: string;
  onUpload: () => void;
  onClose: () => void;
}

type Phase = 'form' | 'uploading' | 'extracting' | 'done' | 'error';

function stringifyError(detail: unknown): string {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => (e as Record<string, unknown>).msg || JSON.stringify(e)).join('; ');
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return 'Upload failed.';
}

export function ScheduleUploadModal({ projectId, token, onUpload, onClose }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [scheduleType, setScheduleType] = useState('');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [extractionMode, setExtractionMode] = useState<'lattice' | 'stream'>('lattice');
  const [headerRow, setHeaderRow] = useState(0);
  const [roomColumn, setRoomColumn] = useState(0);
  const [phase, setPhase] = useState<Phase>('form');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [extractionStatus, setExtractionStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [uploadedScheduleId, setUploadedScheduleId] = useState<number | null>(null);
  const [existingTypes, setExistingTypes] = useState<ScheduleTypeInfo[]>([]);
  const [fuzzySuggestion, setFuzzySuggestion] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typeInputRef = useRef<HTMLInputElement>(null);
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const MAX_FILE_SIZE = 50 * 1024 * 1024;

  const typeNames = existingTypes.map(t => t.type);
  const normalizedType = toTitleCase(scheduleType.trim());
  const matchedTypeInfo = existingTypes.find(t => t.type.toLowerCase() === normalizedType.toLowerCase());

  const filteredTypes = existingTypes.filter(t =>
    !scheduleType.trim() || t.type.toLowerCase().includes(scheduleType.trim().toLowerCase())
  );

  useEffect(() => {
    getProjectScheduleTypes(token, projectId)
      .then(types => setExistingTypes(types))
      .catch(err => console.warn('Failed to load schedule types:', err));
  }, [projectId, token]);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showTypeDropdown &&
        typeDropdownRef.current &&
        !typeDropdownRef.current.contains(e.target as Node) &&
        typeInputRef.current &&
        !typeInputRef.current.contains(e.target as Node)
      ) {
        setShowTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTypeDropdown]);

  useEffect(() => {
    if (phase !== 'extracting' || !uploadedScheduleId) return;

    pollingRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/schedules/${uploadedScheduleId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();

        if (data.extraction_status === 'ready') {
          clearInterval(pollingRef.current!);
          const rows = data.current_version?.total_rows ?? 0;
          const matched = data.current_version?.matched_count ?? 0;
          setExtractionStatus(`Done — ${rows} rows extracted, ${matched} rooms matched.`);
          setPhase('done');
        } else if (data.extraction_status === 'failed') {
          clearInterval(pollingRef.current!);
          setError(stringifyError(data.extraction_error || 'Table extraction failed.'));
          setPhase('error');
        }
      } catch {
        // silently retry
      }
    }, 2000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [phase, uploadedScheduleId, token]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.type !== 'application/pdf') {
      setError('Please upload a PDF file only.');
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      setError('File too large. Maximum size is 50MB.');
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  const handleTypeChange = (value: string) => {
    setScheduleType(value);
    setError(null);
    const trimmed = value.trim();
    if (!trimmed) {
      setFuzzySuggestion(null);
      return;
    }
    const normalized = toTitleCase(trimmed);
    if (typeNames.some(t => t.toLowerCase() === normalized.toLowerCase())) {
      setFuzzySuggestion(null);
      return;
    }
    const match = fuzzyMatchType(trimmed, typeNames);
    setFuzzySuggestion(match && match.toLowerCase() !== normalized.toLowerCase() ? match : null);
  };

  const effectiveScheduleType = normalizedType;

  const handleUpload = () => {
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }

    if (!effectiveScheduleType) {
      setError('Please enter a schedule type.');
      return;
    }

    if (!scheduleDate) {
      setError('Please select a schedule date.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('schedule_type', effectiveScheduleType);
    formData.append('schedule_date', scheduleDate);
    formData.append('extraction_mode', extractionMode);
    formData.append('header_row', headerRow.toString());
    formData.append('room_column', roomColumn.toString());

    setPhase('uploading');
    setUploadProgress(0);
    setError(null);

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        setUploadProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          setUploadedScheduleId(data.schedule_id);
          setExtractionStatus('Extracting table data from PDF…');
          setPhase('extracting');
        } catch {
          setError('Upload succeeded but could not read response.');
          setPhase('error');
        }
      } else {
        try {
          const data = JSON.parse(xhr.responseText);
          setError(stringifyError(data.detail));
        } catch {
          setError('Upload failed. Please try again.');
        }
        setPhase('error');
      }
    });

    xhr.addEventListener('error', () => {
      setError('Network error. Please try again.');
      setPhase('error');
    });

    xhr.open('POST', `/api/projects/${projectId}/schedules`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  };

  const getDisplayName = () => {
    return effectiveScheduleType || 'Schedule';
  };

  const isBusy = phase === 'uploading' || phase === 'extracting';

  const handleClose = () => {
    if (uploadedScheduleId !== null) onUpload();
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px' }}>Upload Schedule</h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '0 4px',
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px' }}>
          {phase !== 'form' && (
            <div
              style={{
                background: phase === 'error' ? '#fff0f0' : phase === 'done' ? '#f0fff4' : '#f0f4ff',
                border: `1px solid ${phase === 'error' ? '#f5c6c6' : phase === 'done' ? '#a3d9a5' : '#c0cff8'}`,
                borderRadius: '6px',
                padding: '16px',
                marginBottom: '16px',
              }}
            >
              {phase === 'uploading' && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
                    <span>Uploading PDF…</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div style={{ height: '8px', background: '#dce6fd', borderRadius: '4px', overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${uploadProgress}%`,
                        background: '#3b72f0',
                        transition: 'width 0.2s ease',
                      }}
                    />
                  </div>
                </>
              )}

              {phase === 'extracting' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px' }}>
                  <span style={{ fontSize: '20px', animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
                  <div>
                    <div style={{ fontWeight: 500, marginBottom: '2px' }}>Extracting table data…</div>
                    <div style={{ color: '#555', fontSize: '13px' }}>This may take a moment for large PDFs.</div>
                  </div>
                </div>
              )}

              {phase === 'done' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px' }}>
                  <span style={{ fontSize: '20px' }}>✅</span>
                  <div>
                    <div style={{ fontWeight: 500, marginBottom: '2px' }}>Schedule ready</div>
                    <div style={{ color: '#555', fontSize: '13px' }}>{extractionStatus}</div>
                  </div>
                </div>
              )}

              {phase === 'error' && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '14px' }}>
                  <span style={{ fontSize: '20px' }}>❌</span>
                  <div>
                    <div style={{ fontWeight: 500, marginBottom: '2px', color: '#c00' }}>Upload failed</div>
                    <div style={{ color: '#c00', fontSize: '13px' }}>{error}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {phase === 'form' && (
            <>
              {error && (
                <div
                  style={{
                    background: '#fee',
                    color: '#c00',
                    padding: '10px 12px',
                    borderRadius: '4px',
                    marginBottom: '16px',
                    fontSize: '14px',
                  }}
                >
                  {error}
                </div>
              )}

              {/* File Upload */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
                  PDF File <span style={{ color: '#c00' }}>*</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: file ? '#e8f5e9' : '#f5f5f5',
                    border: '2px dashed ' + (file ? '#4caf50' : '#ccc'),
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  {file ? (
                    <>
                      <span style={{ fontSize: '18px' }}>✓</span>
                      <span>{file.name}</span>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: '18px' }}>+</span>
                      <span>Select PDF</span>
                    </>
                  )}
                </button>
                <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#666' }}>
                  PDF files only. Maximum size 50MB.
                </p>
              </div>

              {/* Schedule Type — custom dropdown like trade selector */}
              <div style={{ marginBottom: '16px' }} ref={typeDropdownRef}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
                  Schedule Type <span style={{ color: '#c00' }}>*</span>
                </label>

                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      background: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      boxSizing: 'border-box',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ color: scheduleType ? '#333' : '#999' }}>
                      {scheduleType || 'Select or type a schedule type...'}
                    </span>
                    <span style={{ marginLeft: '8px', fontSize: '10px' }}>▼</span>
                  </button>

                  {matchedTypeInfo && matchedTypeInfo.version_count > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        right: '28px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: '#007bff',
                        color: '#fff',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: 600,
                      }}
                    >
                      v{matchedTypeInfo.version_count + 1}
                    </span>
                  )}

                  {showTypeDropdown && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 1001,
                        marginTop: '2px',
                      }}
                    >
                      <input
                        ref={typeInputRef}
                        type="text"
                        value={scheduleType}
                        onChange={(e) => handleTypeChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            setShowTypeDropdown(false);
                          }
                        }}
                        placeholder="Type to search or enter new type..."
                        autoFocus
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: 'none',
                          borderBottom: '1px solid #eee',
                          fontSize: '13px',
                          boxSizing: 'border-box',
                          outline: 'none',
                        }}
                      />
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {filteredTypes.length === 0 && scheduleType.trim() && (
                          <div
                            style={{
                              padding: '8px 12px',
                              fontSize: '13px',
                              color: '#999',
                              fontStyle: 'italic',
                            }}
                          >
                            No matching types — will create "{toTitleCase(scheduleType.trim())}"
                          </div>
                        )}
                        {filteredTypes.map((t) => (
                          <div
                            key={t.type}
                            onClick={() => {
                              setScheduleType(t.type);
                              setShowTypeDropdown(false);
                              setFuzzySuggestion(null);
                              setError(null);
                            }}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              background: matchedTypeInfo?.type === t.type ? '#e3f2fd' : 'transparent',
                              borderLeft: matchedTypeInfo?.type === t.type ? '3px solid #1976d2' : '3px solid transparent',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.background = matchedTypeInfo?.type === t.type ? '#e3f2fd' : 'transparent'}
                          >
                            {t.type}
                            {t.version_count > 0 && (
                              <span style={{ marginLeft: '8px', fontSize: '11px', color: '#007bff', fontWeight: 600 }}>
                                v{t.version_count}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      {scheduleType.trim() && !typeNames.some(t => t.toLowerCase() === normalizedType.toLowerCase()) && (
                        <div style={{ borderTop: '1px solid #eee', padding: '8px' }}>
                          <button
                            onClick={() => {
                              setScheduleType(toTitleCase(scheduleType.trim()));
                              setShowTypeDropdown(false);
                              setFuzzySuggestion(null);
                              setError(null);
                            }}
                            style={{
                              width: '100%',
                              padding: '8px',
                              background: '#28a745',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: 'bold',
                            }}
                          >
                            + Create "{toTitleCase(scheduleType.trim())}"
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {matchedTypeInfo && matchedTypeInfo.version_count > 0 && (
                  <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#856404', background: '#fff3cd', padding: '6px 10px', borderRadius: '4px', border: '1px solid #ffc107' }}>
                    A "{matchedTypeInfo.type}" schedule already exists (v{matchedTypeInfo.version_count}). This upload will become v{matchedTypeInfo.version_count + 1}.
                  </p>
                )}

                {fuzzySuggestion && !showTypeDropdown && (
                  <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#856404', background: '#fff3cd', padding: '6px 10px', borderRadius: '4px', border: '1px solid #ffc107' }}>
                    Did you mean "{fuzzySuggestion}"?{' '}
                    <button
                      onClick={() => {
                        setScheduleType(fuzzySuggestion);
                        setFuzzySuggestion(null);
                      }}
                      style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', textDecoration: 'underline', fontSize: '12px', padding: 0 }}
                    >
                      Use {fuzzySuggestion}
                    </button>
                  </p>
                )}
              </div>

              {/* Schedule Date */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
                  Schedule Date <span style={{ color: '#c00' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => {
                      setScheduleDate(e.target.value);
                      setError(null);
                    }}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                  <button
                    onClick={() => {
                      setScheduleDate(new Date().toISOString().split('T')[0]);
                      setError(null);
                    }}
                    style={{
                      padding: '10px 16px',
                      background: '#e8f5e9',
                      border: '1px solid #4caf50',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#2e7d32',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Today
                  </button>
                </div>
              </div>

              {/* Advanced Settings Toggle */}
              <div style={{ marginBottom: '16px' }}>
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '0',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#007bff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <span>{showAdvanced ? '▼' : '▶'}</span>
                  Advanced Settings
                </button>
              </div>

              {showAdvanced && (
                <div
                  style={{
                    background: '#f8f9fa',
                    padding: '16px',
                    borderRadius: '4px',
                    marginBottom: '16px',
                  }}
                >
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '13px' }}>
                      Extraction Mode
                    </label>
                    <select
                      value={extractionMode}
                      onChange={(e) => setExtractionMode(e.target.value as 'lattice' | 'stream')}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="lattice">Lattice (tables with borders)</option>
                      <option value="stream">Stream (whitespace-separated)</option>
                    </select>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '13px' }}>
                        Header Row
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={10}
                        value={headerRow}
                        onChange={(e) => setHeaderRow(parseInt(e.target.value) || 0)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '13px' }}>
                        Room Column
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={20}
                        value={roomColumn}
                        onChange={(e) => setRoomColumn(parseInt(e.target.value) || 0)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 20px',
            borderTop: '1px solid #eee',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
          }}
        >
          {phase === 'done' ? (
            <button
              onClick={handleClose}
              style={{
                padding: '10px 20px',
                background: '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Done
            </button>
          ) : phase === 'error' ? (
            <>
              <button
                onClick={() => { setPhase('form'); setError(null); }}
                style={{
                  padding: '10px 20px',
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Try Again
              </button>
              <button
                onClick={handleClose}
                style={{
                  padding: '10px 20px',
                  background: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Close
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleClose}
                style={{
                  padding: '10px 20px',
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                {isBusy ? 'Close' : 'Cancel'}
              </button>
              <button
                onClick={handleUpload}
                disabled={isBusy || !file || !effectiveScheduleType || !scheduleDate}
                style={{
                  padding: '10px 20px',
                  background: isBusy || !file || !effectiveScheduleType || !scheduleDate ? '#ccc' : '#007bff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isBusy || !file || !effectiveScheduleType || !scheduleDate ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {isBusy ? 'Processing…' : `Upload ${getDisplayName()}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}